/**
 * Temporary Mixpanel event registry.
 *
 * Goal: keep a single, searchable list of analytics events we intend to track,
 * along with their implementation status and the primary callsites.
 *
 * Note: This file is meant to be removed/converted once we have a proper
 * analytics spec + ownership process.
 */

import { MixpanelSurfaceArea } from './surfaceAreas';

export const MIXPANEL_EVENTS = {
  // Corporations tab
  CORPORATIONS_CHART_VIEW_VIEWED: 'CORPORATIONS_CHART_VIEW_VIEWED',
  CORPORATIONS_SERVICE_CLICKED: 'CORPORATIONS_SERVICE_CLICKED',
  CORPORATIONS_ENTITY_ADDED: 'CORPORATIONS_ENTITY_ADDED',

  // Entity management (corporation detail pages)
  ENTITY_GENERAL_INFO_UPDATED: 'ENTITY_GENERAL_INFO_UPDATED',
  ENTITY_REGISTERED_AGENT_UPDATED: 'ENTITY_REGISTERED_AGENT_UPDATED',
  ENTITY_REGISTERED_ADDRESS_ADDED: 'ENTITY_REGISTERED_ADDRESS_ADDED',
  ENTITY_BUSINESS_PRESENCE_ADDED: 'ENTITY_BUSINESS_PRESENCE_ADDED',
  ENTITY_REGISTRATION_ADDED: 'ENTITY_REGISTRATION_ADDED',
  ENTITY_CREDENTIAL_ADDED: 'ENTITY_CREDENTIAL_ADDED',
  ENTITY_TAX_DETAILS_ADDED: 'ENTITY_TAX_DETAILS_ADDED',
  ENTITY_KEY_PERSON_ADDED: 'ENTITY_KEY_PERSON_ADDED',
  MAILROOM_MAIL_VIEWED: 'MAILROOM_MAIL_VIEWED',
  OBLIGATION_ACKNOWLEDGEMENT_UPLOADED: 'OBLIGATION_ACKNOWLEDGEMENT_UPLOADED',

  // Calendar / compliance monitor
  CALENDAR_FILE_WITH_COMMENDA_SELECTED: 'CALENDAR_FILE_WITH_COMMENDA_SELECTED',
  CALENDAR_FILING_ARCHIVED: 'CALENDAR_FILING_ARCHIVED',
  CALENDAR_CUSTOM_FILING_ADDED: 'CALENDAR_CUSTOM_FILING_ADDED',
  CALENDAR_TRY_COMPLIANCE_GET_STARTED_CLICKED:
    'CALENDAR_TRY_COMPLIANCE_GET_STARTED_CLICKED',
  COMMENDAPEDIA_VIEWED_ON_OBLIGATION: 'COMMENDAPEDIA_VIEWED_ON_OBLIGATION',
  CALENDAR_ENTITY_SELECTOR_CHANGED: 'CALENDAR_ENTITY_SELECTOR_CHANGED',

  // Tax and Accounting App
  TNA_FILE_WITH_COMMENDA_SELECTED: 'TNA_FILE_WITH_COMMENDA_SELECTED',
  TNA_TRY_COMPLIANCE_GET_STARTED_CLICKED:
    'TNA_TRY_COMPLIANCE_GET_STARTED_CLICKED',
  TNA_FILING_CLICKED: 'TNA_FILING_CLICKED',
  TNA_ACCOUNTING_UPLOAD_CLICKED: 'TNA_ACCOUNTING_UPLOAD_CLICKED',
  TNA_ACCOUNTING_VIEW_DOWNLOAD_CLICKED: 'TNA_ACCOUNTING_VIEW_DOWNLOAD_CLICKED',
  TNA_SERVICE_REQUEST_CTA_CLICKED: 'TNA_SERVICE_REQUEST_CTA_CLICKED',
  TNA_ONBOARDING_FORM_SUBMITTED: 'TNA_ONBOARDING_FORM_SUBMITTED',
  TNA_ENTITY_SELECTOR_CHANGED: 'TNA_ENTITY_SELECTOR_CHANGED',
  TNA_COMPLIANCE_BASIC_GET_CLICKED: 'TNA_COMPLIANCE_BASIC_GET_CLICKED',
  TNA_ENTITLEMENT_ACTIVATED: 'TNA_ENTITLEMENT_ACTIVATED',
  ACCOUNTANT_EMAIL_COPIED: 'ACCOUNTANT_EMAIL_COPIED',

  // Transfer Pricing App
  TP_ADD_POLICY_CLICKED: 'TP_ADD_POLICY_CLICKED',
  TP_REQUEST_NEW_REPORT_CLICKED: 'TP_REQUEST_NEW_REPORT_CLICKED',
  TP_BENCHMARKING_FORM_SUBMITTED: 'TP_BENCHMARKING_FORM_SUBMITTED',
  TP_SERVICE_REQUEST_CLICKED: 'TP_SERVICE_REQUEST_CLICKED',
  TP_BENCHMARKING_STUDY_VIEW_DOWNLOAD: 'TP_BENCHMARKING_STUDY_VIEW_DOWNLOAD',
  TP_DOCUMENT_ADD_CLICKED: 'TP_DOCUMENT_ADD_CLICKED',
  TP_DOCUMENT_FORM_SUBMITTED: 'TP_DOCUMENT_FORM_SUBMITTED',
  TP_DOCUMENT_ROW_CLICKED: 'TP_DOCUMENT_ROW_CLICKED',

  HOME_ACTIVE_SERVICE_CLICKED: 'HOME_ACTIVE_SERVICE_CLICKED',
  HOME_TASK_CLICKED: 'HOME_TASK_CLICKED',

  // Services Tab
  SERVICES_QUICK_ACTION_USED: 'SERVICES_QUICK_ACTION_USED',
  SERVICES_VIEW_BY_COUNTRY_CLICKED: 'SERVICES_VIEW_BY_COUNTRY_CLICKED',
  SERVICES_SEARCHED: 'SERVICES_SEARCHED',
  SERVICES_REQUEST_SUBMITTED: 'SERVICES_REQUEST_SUBMITTED',
  SERVICES_BUY_CLICKED: 'SERVICES_BUY_CLICKED',

  // Settings
  MEMBER_ADDED_OR_REMOVED: 'MEMBER_ADDED_OR_REMOVED',
  MEMBER_ROLE_UPDATED: 'MEMBER_ROLE_UPDATED',
  DOWNLOADED_INVOICE: 'DOWNLOADED_INVOICE',
  MCP_ACCESS_CHANGED: 'MCP_ACCESS_CHANGED',

  // Workflow & Form Builder
  WORKFLOW_ADDED: 'WORKFLOW_ADDED',
  WORKFLOW_VERSION_ADDED: 'WORKFLOW_VERSION_ADDED',
  WORKFLOW_PUBLISHED: 'WORKFLOW_PUBLISHED',
  FORM_CREATED: 'FORM_CREATED',
  FORM_PUBLISHED: 'FORM_PUBLISHED',

  // ECM Portal
  ECM_LOGIN: 'ECM_LOGIN',
  ECM_ADD_CERTIFICATE_CLICKED: 'ECM_ADD_CERTIFICATE_CLICKED',
  ECM_SAVE_CERTIFICATE_CLICKED: 'ECM_SAVE_CERTIFICATE_CLICKED',

  // Threads
  MESSAGE_SENT: 'MESSAGE_SENT',
  DOCUMENT_UPLOADED_ON_THREADS: 'DOCUMENT_UPLOADED_ON_THREADS',
  DOCUMENT_UPLOAD_FAILED_ON_THREADS: 'DOCUMENT_UPLOAD_FAILED_ON_THREADS',
  PINNED_MESSAGE: 'PINNED_MESSAGE',
  DOWNLOADED_DOCUMENT_FROM_THREADS: 'DOWNLOADED_DOCUMENT_FROM_THREADS',
  REPLY_TO_MESSAGE_USED: 'REPLY_TO_MESSAGE_USED',
  STANDARD_TEMPLATE_USED: 'STANDARD_TEMPLATE_USED',
  AI_GENERATION_USED: 'AI_GENERATION_USED',
  THREAD_AGENT_MENTIONED: 'THREAD_AGENT_MENTIONED',
  THREAD_AGENT_FEEDBACK_GIVEN: 'THREAD_AGENT_FEEDBACK_GIVEN',
  THREAD_AGENT_MUTED: 'THREAD_AGENT_MUTED',
  THREAD_AGENT_RESUMED: 'THREAD_AGENT_RESUMED',

  // Documents Tab
  DOWNLOADED_DOCUMENT: 'DOWNLOADED_DOCUMENT',
  PREVIEW_DOCUMENT: 'PREVIEW_DOCUMENT',
  ADD_DOCUMENT_BUTTON_CLICKS: 'ADD_DOCUMENT_BUTTON_CLICKS',
  ADDED_DOCUMENT: 'ADDED_DOCUMENT',

  // Global Indirect Tax (GIT)
  GIT_TAB_CLICKED: 'GIT_TAB_CLICKED',
  IMPORT_TRANSACTION_CLICKED: 'IMPORT_TRANSACTION_CLICKED',
  CONNECT_INTEGRATION_CLICKED: 'CONNECT_INTEGRATION_CLICKED',
  ADD_REGISTRATIONS_CLICKED: 'ADD_REGISTRATIONS_CLICKED',
  GENERATE_NEXUS_CLICKED: 'GENERATE_NEXUS_CLICKED',
  CERTIFICATE_ACTION_TAKEN: 'CERTIFICATE_ACTION_TAKEN',
  APPROVE_FILING_CLICKED: 'APPROVE_FILING_CLICKED',
  TAX_LIABILITY_REPORT_CLICKED: 'TAX_LIABILITY_REPORT_CLICKED',
  FILING_DETAIL_CLICKED: 'FILING_DETAIL_CLICKED',
  PRODUCT_MAPPED: 'PRODUCT_MAPPED',
  ADD_EXEMPTION_CERTIFICATE_CLICKED: 'ADD_EXEMPTION_CERTIFICATE_CLICKED',

  // Affiliate App
  AFFILIATE_ACCOUNT_SWITCHED: 'affiliate_account_switched',
  AFFILIATE_PROPOSAL_CREATED: 'affiliate_proposal_created',
  AFFILIATE_CLIENT_ADDED: 'affiliate_client_added',
  AFFILIATE_MEMBER_ADDED: 'affiliate_member_added',
  AFFILIATE_MEMBER_ROLE_UPDATED: 'affiliate_member_role_updated',
  AFFILIATE_MEMBER_REMOVED: 'affiliate_member_removed',
  AFFILIATE_CLIENT_ACCESSED: 'affiliate_client_accessed',

  // Admin App Agents
  ASSIGNED_SERVICE_REQUEST: 'assigned_service_request',
  BULK_ASSIGNED_SERVICE_REQUEST: 'bulk_assigned_service_request',
  ADDED_AGENT_FIRM: 'added_agent_firm',
  ADDED_AGENT_FIRM_MEMBER: 'added_agent_firm_member',

  // Agent App
  ISSUE_REPORTED: 'issue_reported',
  CREATE_INFORMATION_REQUEST: 'create_information_request',
  SERVICE_INFORMATION_TAB_ACCESS: 'service_information_tab_access',
  USING_RESPONSE_TEMPLATES: 'using_response_templates',
  UPDATES_IN_INTERNAL_CHAT: 'updates_in_internal_chat',

  // Sales Tax Agent
  FILING_MARKED_COMPLETE: 'filing_marked_complete',
  STATE_REGISTRATION_MARKED_VALID: 'state_registration_marked_valid',
  NEW_STATE_REGISTRATION_CLICKED: 'new_state_registration_clicked',

  // Service Delivery
  DOWNLOADED_DOCUMENT_FROM_SERVICE_REQUEST_DRAWER:
    'downloaded_document_from_service_request_drawer',

  // Demo-session bar (only fires in demo env, only for /quick-demo signups)
  DEMO_BAR_CTA_CLICKED: 'demo_bar_cta_clicked',
  DEMO_MEETING_BOOKED: 'demo_meeting_booked',
} as const;

export type MixpanelEventName =
  (typeof MIXPANEL_EVENTS)[keyof typeof MIXPANEL_EVENTS];

export type MixpanelEventStatus = 'implemented' | 'planned';

export type MixpanelEventRegistryItem = {
  name: MixpanelEventName;
  status: MixpanelEventStatus;
  area: MixpanelSurfaceArea;
  description: string;
  primary_callsites: string[];
};

export const MIXPANEL_EVENT_REGISTRY: readonly MixpanelEventRegistryItem[] = [
  {
    name: MIXPANEL_EVENTS.CORPORATIONS_CHART_VIEW_VIEWED,
    status: 'implemented',
    area: 'corporations_tab',
    description: 'User views chart (grid) view on Corporations tab.',
    primary_callsites: ['apps/enterprise/pages/corporations.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.CORPORATIONS_ENTITY_ADDED,
    status: 'implemented',
    area: 'corporations_tab',
    description: 'User adds an entity from the Corporations tab.',
    primary_callsites: [
      'apps/enterprise/pages/corporations.tsx',
      'apps/enterprise/components/Corporations/Home/CorporationsTableView.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.CORPORATIONS_SERVICE_CLICKED,
    status: 'implemented',
    area: 'corporations_tab',
    description: 'User clicks a service in the corporation services table.',
    primary_callsites: ['apps/enterprise/components/Corporations/Services.tsx'],
  },

  {
    name: MIXPANEL_EVENTS.ENTITY_GENERAL_INFO_UPDATED,
    status: 'implemented',
    area: 'entity_management',
    description:
      'User adds/updates general business information (description/code/ID/etc.) in Business Information > General.',
    primary_callsites: [
      'packages/ui/Corporation/BusinessInformation/BusinessInformationTabs/GeneralInformation/index.tsx',
      'packages/ui/Corporation/BusinessInformation/BusinessInformationTabs/GeneralInformation/FormationDocuments/UploadFormationDocumentModal.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.ENTITY_REGISTERED_AGENT_UPDATED,
    status: 'implemented',
    area: 'entity_management',
    description:
      'User adds/updates registered agent information in Business Information.',
    primary_callsites: [
      'packages/ui/Corporation/BusinessInformation/BusinessInformationTabs/GeneralInformation/RegisteredAgent/RegisteredAgentModal.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.ENTITY_REGISTERED_ADDRESS_ADDED,
    status: 'implemented',
    area: 'entity_management',
    description:
      'User adds a registered address in Business Information (registered address / business presence).',
    primary_callsites: [
      'packages/ui/Corporation/BusinessInformation/BusinessInformationTabs/Locations/Modals/AddRegisteredAddressModal.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.ENTITY_BUSINESS_PRESENCE_ADDED,
    status: 'implemented',
    area: 'entity_management',
    description: 'User adds a business presence in Business Information.',
    primary_callsites: [
      'packages/ui/Corporation/BusinessInformation/BusinessInformationTabs/Locations/Modals/AddLocationModal.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.ENTITY_REGISTRATION_ADDED,
    status: 'implemented',
    area: 'entity_management',
    description:
      'User adds a registration in Business Information > Registrations.',
    primary_callsites: [
      'packages/ui/Corporation/BusinessInformation/BusinessInformationTabs/Registration/Modals/AddRegistrationModal.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.ENTITY_CREDENTIAL_ADDED,
    status: 'implemented',
    area: 'entity_management',
    description:
      'User adds a credential in Business Information > Credentials.',
    primary_callsites: [
      'packages/ui/Corporation/BusinessInformation/BusinessInformationTabs/Credentials/Modals/AddCredentialModal/index.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.ENTITY_TAX_DETAILS_ADDED,
    status: 'implemented',
    area: 'entity_management',
    description: 'User adds tax details in Business Information > Tax.',
    primary_callsites: [
      'packages/ui/Corporation/BusinessInformation/BusinessInformationTabs/Tax/index.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.ENTITY_KEY_PERSON_ADDED,
    status: 'implemented',
    area: 'entity_management',
    description: 'User adds a key person for an entity.',
    primary_callsites: [
      'packages/ui/Corporation/KeyPersons/Modals/AddKeyPersonModal.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.MAILROOM_MAIL_VIEWED,
    status: 'implemented',
    area: 'mailroom',
    description: 'User views a mail item in Mailroom.',
    primary_callsites: ['apps/enterprise/pages/corporation/[id]/index.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.OBLIGATION_ACKNOWLEDGEMENT_UPLOADED,
    status: 'implemented',
    area: 'calendar',
    description: 'User uploads an acknowledgement on an obligation/filing.',
    primary_callsites: [
      'packages/ui/molecules/filings/modals/CompleteFilingModal.tsx',
      'packages/ui/Corporation/Monitor/Filings/modals/CompleteFilingModal.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.CALENDAR_FILE_WITH_COMMENDA_SELECTED,
    status: 'implemented',
    area: 'calendar',
    description:
      'User selects “File with Commenda” during compliance calendar generation.',
    primary_callsites: [
      'packages/ui/Corporation/Monitor/Onboarding/ComplianceOnboardingModal.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.CALENDAR_FILING_ARCHIVED,
    status: 'implemented',
    area: 'calendar',
    description: 'User archives a filing.',
    primary_callsites: [
      'packages/ui/Corporation/Monitor/Filings/modals/ArchiveFilingModal.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.CALENDAR_CUSTOM_FILING_ADDED,
    status: 'implemented',
    area: 'calendar',
    description: 'User adds a custom filing.',
    primary_callsites: [
      'packages/ui/Corporation/Monitor/Filings/AddCustomFilingModal.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.CALENDAR_TRY_COMPLIANCE_GET_STARTED_CLICKED,
    status: 'implemented',
    area: 'calendar',
    description: 'User clicks “Get started” on “Try compliance with Commenda”.',
    primary_callsites: ['packages/ui/Corporation/Monitor/UpsellCompliance.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.COMMENDAPEDIA_VIEWED_ON_OBLIGATION,
    status: 'implemented',
    area: 'calendar',
    description: 'User views Commendapedia on an obligation/filing.',
    primary_callsites: [
      'apps/enterprise/pages/compliance-monitor/compliance/[id].tsx',
    ],
  },

  // Tax and Accounting App
  {
    name: MIXPANEL_EVENTS.TNA_FILE_WITH_COMMENDA_SELECTED,
    status: 'implemented',
    area: 'ta_app',
    description:
      'User selects "File with Commenda" during compliance calendar generation in T&A.',
    primary_callsites: [
      'apps/enterprise/components/TaxAndReporting/TaxAndReportingPageWrapper.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.TNA_TRY_COMPLIANCE_GET_STARTED_CLICKED,
    status: 'implemented',
    area: 'ta_app',
    description:
      'User clicks "Get started" on "Try compliance with Commenda" in T&A.',
    primary_callsites: [
      'apps/enterprise/components/TaxAndReporting/TaxAndReportingPageWrapper.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.TNA_FILING_CLICKED,
    status: 'implemented',
    area: 'ta_app',
    description: 'User clicks on a filing in Tax and Reporting.',
    primary_callsites: [
      'apps/enterprise/components/TaxAndReporting/FilingsSummary.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.TNA_ACCOUNTING_UPLOAD_CLICKED,
    status: 'implemented',
    area: 'ta_app',
    description: 'User clicks on upload in the accounting service request.',
    primary_callsites: [
      'apps/enterprise/components/Accounting/AccountingDocuments.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.TNA_ACCOUNTING_VIEW_DOWNLOAD_CLICKED,
    status: 'implemented',
    area: 'ta_app',
    description:
      'User clicks on view/downloads on the agent upload in accounting service request.',
    primary_callsites: [
      'apps/enterprise/components/Accounting/AccountingDocuments.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.TNA_SERVICE_REQUEST_CTA_CLICKED,
    status: 'implemented',
    area: 'ta_app',
    description:
      'User clicks on any of the service request CTAs in Tax and Reporting tab.',
    primary_callsites: [
      'apps/enterprise/components/TaxAndReporting/TaxAndReportingPageWrapper.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.TNA_ONBOARDING_FORM_SUBMITTED,
    status: 'implemented',
    area: 'ta_app',
    description: 'User clicks submit on the onboarding form for T&A.',
    primary_callsites: [
      'apps/enterprise/components/TaxAndReporting/OnboardingChecklist.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.TNA_ENTITY_SELECTOR_CHANGED,
    status: 'implemented',
    area: 'ta_app',
    description:
      'User clicks on another entity in the entity selector on Tax and Reporting or Accounting tab.',
    primary_callsites: [
      'apps/enterprise/components/TaxAndReporting/TaxAndReportingPageWrapper.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.TNA_COMPLIANCE_BASIC_GET_CLICKED,
    status: 'implemented',
    area: 'ta_app',
    description:
      'User clicks on "Get Compliance Basic" in the self serve flow for Tax and Reporting.',
    primary_callsites: [
      'packages/ui/billing/utils/productPackages/compliance.ts',
    ],
  },
  {
    name: MIXPANEL_EVENTS.TNA_ENTITLEMENT_ACTIVATED,
    status: 'implemented',
    area: 'ta_app',
    description: 'User uses their entitlement and clicks on activate.',
    primary_callsites: [
      'apps/enterprise/components/TaxAndReporting/TaxAndReportingPageWrapper.tsx',
    ],
  },

  // Transfer Pricing App
  {
    name: MIXPANEL_EVENTS.TP_ADD_POLICY_CLICKED,
    status: 'implemented',
    area: 'tp_app',
    description: 'User clicks on add policy button in policies tab.',
    primary_callsites: [
      'apps/enterprise/components/TransferPricingNew/TransferPricingPoliciesTabNew.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.TP_REQUEST_NEW_REPORT_CLICKED,
    status: 'implemented',
    area: 'tp_app',
    description:
      'User clicks on Request a new report or add report on benchmarking tab.',
    primary_callsites: [
      'apps/enterprise/components/TransferPricingBenchmarking/views/InitialHeroView.tsx',
      'apps/enterprise/components/TransferPricingBenchmarking/views/ActivateEntitiesView.tsx',
      'apps/enterprise/components/TransferPricingBenchmarking/views/BenchmarkingDashboard.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.TP_BENCHMARKING_FORM_SUBMITTED,
    status: 'planned',
    area: 'tp_app',
    description:
      'User clicks "submit" in business details form in benchmarking (1st customer step).',
    primary_callsites: [],
  },
  {
    name: MIXPANEL_EVENTS.TP_SERVICE_REQUEST_CLICKED,
    status: 'implemented',
    area: 'tp_app',
    description: 'User clicks on any service request in the benchmarking tab.',
    primary_callsites: [
      'apps/enterprise/components/TransferPricingBenchmarking/views/BenchmarkingDashboard.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.TP_DOCUMENT_ADD_CLICKED,
    status: 'implemented',
    area: 'tp_app',
    description:
      'User picks Request new or Add existing on the TP Documents tab.',
    primary_callsites: [
      'apps/enterprise/components/TransferPricing/documents/AddTransferPricingDocumentMenu.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.TP_DOCUMENT_FORM_SUBMITTED,
    status: 'implemented',
    area: 'tp_app',
    description:
      'User submits the Request new or Add existing TP document form.',
    primary_callsites: [
      'apps/enterprise/components/TransferPricing/documents/useRequestTransferPricingLocalFileForm.ts',
      'apps/enterprise/components/TransferPricing/documents/useAddTransferPricingDocumentForm.ts',
    ],
  },
  {
    name: MIXPANEL_EVENTS.TP_DOCUMENT_ROW_CLICKED,
    status: 'implemented',
    area: 'tp_app',
    description: 'User opens a record from the TP Documents table.',
    primary_callsites: [
      'apps/enterprise/components/TransferPricing/documents/TransferPricingDocumentsTable.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.TP_BENCHMARKING_STUDY_VIEW_DOWNLOAD,
    status: 'implemented',
    area: 'tp_app',
    description:
      'User views/downloads the benchmarking study uploaded by the agent.',
    primary_callsites: [
      'apps/enterprise/components/TransferPricingBenchmarking/BenchmarkingReport/BenchmarkingReport.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.ACCOUNTANT_EMAIL_COPIED,
    status: 'implemented',
    area: 'ta_app',
    description: 'User copies the accountant email to clipboard.',
    primary_callsites: [
      'apps/enterprise/components/TaxAndReporting/AccountantCard.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.CALENDAR_ENTITY_SELECTOR_CHANGED,
    status: 'implemented',
    area: 'calendar',
    description: 'User switches entity on the compliance calendar tab.',
    primary_callsites: ['apps/enterprise/pages/compliance-monitor/index.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.CALENDAR_ENTITY_SELECTOR_CHANGED,
    status: 'implemented',
    area: 'calendar',
    description: 'User switches entity on the compliance calendar tab.',
    primary_callsites: ['apps/enterprise/pages/compliance-monitor/index.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.HOME_ACTIVE_SERVICE_CLICKED,
    status: 'implemented',
    area: 'global_home',
    description: 'User clicks on an active service in the Global Home view.',
    primary_callsites: [
      'apps/enterprise/components/HomeScreen/ActiveServices.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.HOME_TASK_CLICKED,
    status: 'implemented',
    area: 'global_home',
    description: 'User clicks on a task in the Global Home view.',
    primary_callsites: ['apps/enterprise/components/HomeScreen/Tasks.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.AFFILIATE_ACCOUNT_SWITCHED,
    status: 'implemented',
    area: 'affiliate_app',
    description: 'User switches to a client account in the Affiliate App.',
    primary_callsites: ['apps/enterprise/pages/clients.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.AFFILIATE_PROPOSAL_CREATED,
    status: 'implemented',
    area: 'affiliate_app',
    description: 'User creates a new proposal in the Affiliate App.',
    primary_callsites: [
      'apps/enterprise/components/proposals/AddProposalsModal.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.AFFILIATE_CLIENT_ADDED,
    status: 'implemented',
    area: 'affiliate_app',
    description: 'User adds/invites a new client in the Affiliate App.',
    primary_callsites: [
      'apps/enterprise/components/affiliate-firms/AddClientModal.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.AFFILIATE_MEMBER_ADDED,
    status: 'implemented',
    area: 'affiliate_app',
    description: 'User adds a new member to the affiliate firm.',
    primary_callsites: [
      'apps/enterprise/components/affiliate-firms/AddAffiliateMemberModal.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.AFFILIATE_MEMBER_REMOVED,
    status: 'implemented',
    area: 'affiliate_app',
    description: 'User removes a member from the affiliate firm.',
    primary_callsites: [
      'apps/enterprise/components/affiliate-firms/RemoveMemberFromAffiliateFirmModal.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.SERVICES_QUICK_ACTION_USED,
    status: 'implemented',
    area: 'services_tab',
    description:
      'User uses the quick action (Incorporate) in the Services Tab.',
    primary_callsites: [
      'apps/enterprise/components/marketplace-services/components/IncorporateSelector.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.SERVICES_VIEW_BY_COUNTRY_CLICKED,
    status: 'implemented',
    area: 'services_tab',
    description: 'User clicks on a country banner to view services by country.',
    primary_callsites: [
      'apps/enterprise/components/marketplace-services/marketplace-services-page.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.SERVICES_SEARCHED,
    status: 'implemented',
    area: 'services_tab',
    description: 'User searches for a service in the Services Tab.',
    primary_callsites: [
      'apps/enterprise/components/marketplace-services/marketplace-services.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.SERVICES_REQUEST_SUBMITTED,
    status: 'implemented',
    area: 'services_tab',
    description: 'User submits a request for a new service.',
    primary_callsites: [
      'apps/enterprise/components/marketplace-services/contact-us-form.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.SERVICES_BUY_CLICKED,
    status: 'implemented',
    area: 'services_tab',
    description: 'User clicks "Buy Now" for a service.',
    primary_callsites: [
      'apps/enterprise/components/marketplace-services/components/ServiceVariantsModal.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.MEMBER_ADDED_OR_REMOVED,
    status: 'implemented',
    area: 'settings',
    description: 'User adds or removes a member in Settings.',
    primary_callsites: [
      'apps/enterprise/components/settings/Team.tsx',
      'apps/enterprise/components/settings/modals/team/RemoveUserModal.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.MEMBER_ROLE_UPDATED,
    status: 'implemented',
    area: 'settings',
    description:
      "An admin changes a member's role in Settings → Team and roles.",
    primary_callsites: [
      'apps/enterprise/components/settings/modals/team/EditUserRoleModal.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.MCP_ACCESS_CHANGED,
    status: 'implemented',
    area: 'settings',
    description:
      'An admin changes Commenda MCP access — either the Members default or one member\'s override — in Settings → Commenda MCP.',
    primary_callsites: [
      'apps/enterprise/components/settings/commenda-mcp/CommendaMcpSettings.tsx',
      'apps/enterprise/components/settings/commenda-mcp/CommendaMcpMembers.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.DOWNLOADED_INVOICE,
    status: 'implemented',
    area: 'settings',
    description: 'User downloads an invoice.',
    primary_callsites: [
      'apps/enterprise/components/settings/Invoices.tsx',
      'apps/enterprise/components/settings/InvoiceViewDialog.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.WORKFLOW_ADDED,
    status: 'implemented',
    area: 'workflow_and_form_builder',
    description: 'User creates a new workflow template.',
    primary_callsites: [
      'packages/ui/workflow/workflow-templates/forms/CreateWorkflowTemplateForm.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.WORKFLOW_VERSION_ADDED,
    status: 'implemented',
    area: 'workflow_and_form_builder',
    description: 'User adds a new version to a workflow template.',
    primary_callsites: [
      'packages/ui/workflow/workflow-templates/forms/CreateWorkflowTemplateVersionForm.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.WORKFLOW_PUBLISHED,
    status: 'implemented',
    area: 'workflow_and_form_builder',
    description: 'User publishes a workflow template version.',
    primary_callsites: [
      'packages/ui/workflow/workflow-templates/WorkflowEditorToolbar.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.FORM_CREATED,
    status: 'implemented',
    area: 'workflow_and_form_builder',
    description: 'User creates a new form template.',
    primary_callsites: ['packages/ui/form-builder/hooks/useFormBuilder.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.FORM_PUBLISHED,
    status: 'implemented',
    area: 'workflow_and_form_builder',
    description: 'User publishes a form template (sets status to LIVE).',
    primary_callsites: [
      'packages/ui/form-builder/templates/EditableStatusCell.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.ECM_LOGIN,
    status: 'implemented',
    area: 'ecm_portal',
    description: 'User logs in to the ECM portal.',
    primary_callsites: ['apps/exemption-portal/app/page.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.ECM_ADD_CERTIFICATE_CLICKED,
    status: 'implemented',
    area: 'ecm_portal',
    description: 'User clicks on "Add Certificate" in ECM portal.',
    primary_callsites: ['apps/exemption-portal/app/page.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.ECM_SAVE_CERTIFICATE_CLICKED,
    status: 'implemented',
    area: 'ecm_portal',
    description: 'User saves a certificate in ECM portal.',
    primary_callsites: [
      'packages/ui/exemption-portal/forms/ExemptionCertificateForm.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.MESSAGE_SENT,
    status: 'implemented',
    area: 'threads',
    description: 'User sends a message in a thread.',
    primary_callsites: ['packages/ui/chat/ChatRoom/index.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.DOCUMENT_UPLOADED_ON_THREADS,
    status: 'implemented',
    area: 'threads',
    description: 'User uploads a document to a thread.',
    primary_callsites: ['packages/ui/chat/ChatRoom/index.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.DOCUMENT_UPLOAD_FAILED_ON_THREADS,
    status: 'implemented',
    area: 'threads',
    description:
      'A thread attachment failed to upload. Carries failure_stage (presign or put), status_code, file_size_bytes, file_type and duration_ms.',
    primary_callsites: ['packages/ui/chat/ChatRoom/index.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.PINNED_MESSAGE,
    status: 'implemented',
    area: 'threads',
    description: 'User pins a message in a thread.',
    primary_callsites: ['packages/ui/chat/ChatRoom/index.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.DOWNLOADED_DOCUMENT_FROM_THREADS,
    status: 'implemented',
    area: 'threads',
    description: 'User downloads a document from a thread.',
    primary_callsites: ['packages/ui/chat/ChatRoom/index.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.REPLY_TO_MESSAGE_USED,
    status: 'implemented',
    area: 'threads',
    description: 'User replies to a message in a thread.',
    primary_callsites: ['packages/ui/chat/ChatRoom/index.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.STANDARD_TEMPLATE_USED,
    status: 'implemented',
    area: 'threads',
    description: 'User uses a standard template in a thread.',
    primary_callsites: ['packages/ui/chat/ChatRoom/index.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.AI_GENERATION_USED,
    status: 'implemented',
    area: 'threads',
    description: 'User uses AI generation in a thread.',
    primary_callsites: ['packages/ui/chat/ChatRoom/index.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.THREAD_AGENT_MENTIONED,
    status: 'implemented',
    area: 'threads',
    description:
      'User sends a thread message that @mentions the Commenda Agent. Carries chat_room_id and channel.',
    primary_callsites: ['packages/ui/chat/ChatRoom/index.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.THREAD_AGENT_FEEDBACK_GIVEN,
    status: 'implemented',
    area: 'threads',
    description:
      'User rates a Commenda Agent reply in a thread. Carries chat_room_id, channel, message_id and rating (UP or DOWN).',
    primary_callsites: ['packages/ui/chat/hooks/useThreadAgentActions.ts'],
  },
  {
    name: MIXPANEL_EVENTS.THREAD_AGENT_MUTED,
    status: 'implemented',
    area: 'threads',
    description:
      'User mutes the Commenda Agent in a thread channel from a reply. Carries chat_room_id and channel.',
    primary_callsites: ['packages/ui/chat/hooks/useThreadAgentActions.ts'],
  },
  {
    name: MIXPANEL_EVENTS.THREAD_AGENT_RESUMED,
    status: 'implemented',
    area: 'threads',
    description:
      'User resumes the muted Commenda Agent in a thread channel from a reply. Carries chat_room_id and channel.',
    primary_callsites: ['packages/ui/chat/hooks/useThreadAgentActions.ts'],
  },
  {
    name: MIXPANEL_EVENTS.DOWNLOADED_DOCUMENT,
    status: 'implemented',
    area: 'documents_tab',
    description: 'User downloads a document from the Documents tab.',
    primary_callsites: [
      'packages/ui/Corporation/Documents/DocumentsDriveView.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.PREVIEW_DOCUMENT,
    status: 'implemented',
    area: 'documents_tab',
    description: 'User previews a document in the Documents tab.',
    primary_callsites: [
      'packages/ui/Corporation/Documents/DocumentsDriveView.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.ADD_DOCUMENT_BUTTON_CLICKS,
    status: 'implemented',
    area: 'documents_tab',
    description: 'User clicks on the "Add Document" button.',
    primary_callsites: ['apps/enterprise/pages/documents.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.ADDED_AGENT_FIRM_MEMBER,
    status: 'implemented', // Assuming 'implemented' as per surrounding entries
    area: 'admin_app_agents', // Assuming a new area based on surface_area
    description: 'User added a member to an agent firm',
    primary_callsites: [], // No callsites provided, so leaving empty
  },

  // Agent App
  {
    name: MIXPANEL_EVENTS.ISSUE_REPORTED,
    status: 'implemented',
    area: 'agent_app',
    description: 'User reported an issue',
    primary_callsites: [],
  },
  {
    name: MIXPANEL_EVENTS.CREATE_INFORMATION_REQUEST,
    status: 'implemented',
    area: 'agent_app',
    description: 'User created an information request',
    primary_callsites: [],
  },
  {
    name: MIXPANEL_EVENTS.SERVICE_INFORMATION_TAB_ACCESS,
    status: 'implemented',
    area: 'agent_app',
    description: 'User accessed service information tab',
    primary_callsites: [],
  },
  {
    name: MIXPANEL_EVENTS.USING_RESPONSE_TEMPLATES,
    status: 'implemented',
    area: 'agent_app',
    description: 'User used a response template',
    primary_callsites: [],
  },
  {
    name: MIXPANEL_EVENTS.FILING_MARKED_COMPLETE,
    status: 'implemented',
    area: 'agent_app',
    description: 'User marked filing as complete',
    primary_callsites: [],
  },
  {
    name: MIXPANEL_EVENTS.STATE_REGISTRATION_MARKED_VALID,
    status: 'implemented',
    area: 'agent_app',
    description: 'User marked state registration as valid',
    primary_callsites: [],
  },
  {
    name: MIXPANEL_EVENTS.NEW_STATE_REGISTRATION_CLICKED,
    status: 'implemented',
    area: 'agent_app',
    description: 'User clicked new state registration request',
    primary_callsites: [],
  },
  {
    name: MIXPANEL_EVENTS.DOWNLOADED_DOCUMENT_FROM_SERVICE_REQUEST_DRAWER,
    status: 'implemented',
    area: 'ServiceDelivery',
    description:
      'User downloaded a document from the service request drawer documents tab',
    primary_callsites: [],
  },
  {
    name: MIXPANEL_EVENTS.UPDATES_IN_INTERNAL_CHAT,
    status: 'implemented',
    area: 'agent_app',
    description: 'User sent update in internal chat',
    primary_callsites: [],
  },
  {
    name: MIXPANEL_EVENTS.ADDED_DOCUMENT,
    status: 'implemented',
    area: 'documents_tab',
    description: 'User successfully adds a document.',
    primary_callsites: [
      'packages/ui/Corporation/Documents/DocumentsDriveView.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.GIT_TAB_CLICKED,
    status: 'implemented',
    area: 'indirect_tax_app',
    description: 'User clicks on "Global Indirect Tax" Tab.',
    primary_callsites: ['apps/enterprise/components/Layouts/Sidebar.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.IMPORT_TRANSACTION_CLICKED,
    status: 'implemented',
    area: 'indirect_tax_app',
    description: 'User clicks on "Import Transaction".',
    primary_callsites: [
      'packages/ui/sales-tax/transactions-tab/transactions.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.CONNECT_INTEGRATION_CLICKED,
    status: 'implemented',
    area: 'indirect_tax_app',
    description: 'User clicks on "Connect Integration".',
    primary_callsites: [],
  },
  {
    name: MIXPANEL_EVENTS.ADD_REGISTRATIONS_CLICKED,
    status: 'implemented',
    area: 'indirect_tax_app',
    description: 'User clicks on "Add Registrations".',
    primary_callsites: [],
  },
  {
    name: MIXPANEL_EVENTS.GENERATE_NEXUS_CLICKED,
    status: 'implemented',
    area: 'indirect_tax_app',
    description: 'User clicks on "Generate Nexus".',
    primary_callsites: [],
  },
  {
    name: MIXPANEL_EVENTS.CERTIFICATE_ACTION_TAKEN,
    status: 'implemented',
    area: 'indirect_tax_app',
    description: 'User takes an action (accept/reject) on a certificate.',
    primary_callsites: [
      'packages/ui/sales-tax/exemptions-tab/exemptions/ExemptionsTable.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.APPROVE_FILING_CLICKED,
    status: 'implemented',
    area: 'indirect_tax_app',
    description:
      'User clicks on "Approve" under filings tab (bulk or single state approval).',
    primary_callsites: [
      'packages/ui/sales-tax/filing/dashboard/FilingTable.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.TAX_LIABILITY_REPORT_CLICKED,
    status: 'implemented',
    area: 'indirect_tax_app',
    description: 'User clicks on "Tax Liability Report" under Filings Tab.',
    primary_callsites: [
      'packages/ui/sales-tax/filing/dashboard/TaxLiabilityReport.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.FILING_DETAIL_CLICKED,
    status: 'implemented',
    area: 'indirect_tax_app',
    description: 'User clicks into the filing detail page.',
    primary_callsites: [
      'packages/ui/sales-tax/filing/dashboard/FilingTable.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.PRODUCT_MAPPED,
    status: 'implemented',
    area: 'indirect_tax_app',
    description:
      'User maps the product (Clicking on the Product -> Edit Product).',
    primary_callsites: ['packages/ui/sales-tax/products-tab/index.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.ADD_EXEMPTION_CERTIFICATE_CLICKED,
    status: 'implemented',
    area: 'indirect_tax_app',
    description:
      'User clicks on "Add Exemption Certificate" under Exemption Tab.',
    primary_callsites: ['packages/ui/sales-tax/exemptions-tab/index.tsx'],
  },
  {
    name: MIXPANEL_EVENTS.DEMO_BAR_CTA_CLICKED,
    status: 'implemented',
    area: 'commenda_app',
    description:
      'User in a /quick-demo session clicks "Book a setup call" on the demo bar.',
    primary_callsites: [
      'apps/enterprise/components/QuickDemo/DemoSessionBar.tsx',
    ],
  },
  {
    name: MIXPANEL_EVENTS.DEMO_MEETING_BOOKED,
    status: 'implemented',
    area: 'commenda_app',
    description:
      'User completes a Default.com meeting booking from the demo bar.',
    primary_callsites: [
      'apps/enterprise/components/QuickDemo/DemoSessionBar.tsx',
    ],
  },
] as const;
